import { useInfiniteQuery } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import type { KpiListFilters } from "../api/types.js";
import { kpiKeys } from "./kpiQueryKeys.js";

/**
 * A filtered, cursor-paginated page set of configured KPIs (`GET /v1/kpis`).
 * `useInfiniteQuery` so the list view can "load more" — the KPI Manager may face
 * large tenants, so filtering + pagination are server-side (KPI Ecosystem §10),
 * not client-side like the small Hoshin plan grid. Callers flatten
 * `data.pages.flatMap((p) => p.data)`.
 */
export function useKpis(filters: KpiListFilters = {}) {
  const config = useApiClient();
  return useInfiniteQuery({
    queryKey: kpiKeys.list(filters),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      kpiApi.listKpis(
        config,
        pageParam ? { ...filters, cursor: pageParam } : filters,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.hasMore ? (last.nextCursor ?? undefined) : undefined,
    staleTime: 30_000,
  });
}
